import type { FeatureConfig } from '~/lib/builder-types';
import type { TechSpecResolver } from './types';

export const adminPanelTechSpec: TechSpecResolver = (
  stack,
  featureConfig?: FeatureConfig,
) => {
  const isMongo = stack.database === 'mongodb';
  const isPostgres =
    stack.database === 'postgres' || stack.database === 'mysql';

  const features =
    (featureConfig?.options['admin-features'] as string[] | undefined) ?? [];
  const hasImpersonation = features.includes('impersonation');
  const hasAuditLog = features.includes('audit-log') || features.length === 0; // on by default

  return {
    pages: [
      {
        id: 'admin-home',
        path: '/admin',
        label: 'Admin overview',
        description:
          'At-a-glance KPIs: total users, signups today, error rate, pending moderation count',
        authRequired: true,
      },
      {
        id: 'admin-users',
        path: '/admin/users',
        label: 'User management',
        description:
          'Searchable, filterable user table — status, plan, last active, quick actions',
        authRequired: true,
      },
      {
        id: 'admin-user-detail',
        path: '/admin/users/:id',
        label: 'User detail',
        description:
          'Full user profile: account info, subscription, activity log, sessions, and admin actions',
        authRequired: true,
      },
      {
        id: 'admin-content',
        path: '/admin/content',
        label: 'Content moderation',
        description: 'Queue of flagged content — approve, remove, or escalate',
        authRequired: true,
      },
      {
        id: 'admin-settings',
        path: '/admin/settings',
        label: 'App settings',
        description:
          'Feature flags, site-wide config values, maintenance mode toggle',
        authRequired: true,
      },
      ...(hasAuditLog
        ? [
            {
              id: 'admin-audit-log',
              path: '/admin/audit-log',
              label: 'Audit log',
              description:
                'Immutable log of all admin actions with actor, target, before/after state',
              authRequired: true,
            },
          ]
        : []),
    ],
    uxFlows: [
      {
        id: 'suspend-user',
        title: 'Admin suspends or bans a user',
        steps: [
          'Admin searches for user in /admin/users using email, name, or ID',
          'Admin opens /admin/users/:id and reviews account activity',
          'Admin clicks "Suspend" or "Ban" — modal asks for reason and duration',
          'PATCH /api/admin/users/:id/status sets status to "suspended" or "banned" with reason',
          'All active sessions for the user are immediately invalidated server-side',
          'User receives an automated email explaining the action and appeal process',
          hasAuditLog
            ? 'Action recorded in audit_log with before/after state and admin IP'
            : 'Action logged to server logs',
        ],
      },
      {
        id: 'content-moderation',
        title: 'Admin moderates flagged content',
        steps: [
          'Admin visits /admin/content — items sorted by flag count descending',
          'Admin reviews the item (text, images, links) in context',
          'Admin clicks "Approve" (dismiss flags), "Remove" (delete content), or "Escalate" (assign to senior admin)',
          'DELETE /api/admin/content/:id or PATCH /api/admin/content/:id/status updates the record',
          'Content creator is notified via email if content is removed',
          hasAuditLog
            ? 'Moderation action written to audit_log'
            : 'Action logged',
        ],
      },
      {
        id: 'bulk-action',
        title: 'Admin performs a bulk action on users',
        steps: [
          'Admin applies filters in /admin/users (e.g. plan=free, created_at < 30d ago)',
          'Admin selects all matching rows using the "Select all" checkbox',
          'Admin picks a bulk action: export CSV, send email, add tag, or suspend',
          'POST /api/admin/users/bulk with action and list of user IDs (or filter params)',
          'Server processes action in a background job for large sets (> 100 users)',
          'Admin receives an in-app notification or email when the job completes',
        ],
      },
      ...(hasImpersonation
        ? [
            {
              id: 'impersonate-user',
              title: 'Admin impersonates a user',
              steps: [
                'Admin navigates to /admin/users/:id',
                'Admin clicks "Impersonate" — only available to super-admins',
                'POST /api/admin/users/:id/impersonate creates a short-lived impersonation session token',
                'Admin is redirected to the app as the target user with a visible "Impersonation active" banner',
                'All actions taken during impersonation are tagged with the admin actor ID in audit_log',
                'Admin clicks "End impersonation" to return to their own session',
              ],
            },
          ]
        : []),
      {
        id: 'feature-flag',
        title: 'Admin toggles a feature flag',
        steps: [
          'Admin visits /admin/settings',
          'Admin toggles a feature flag switch',
          'PATCH /api/admin/settings updates the key/value pair in DB',
          'App reads flag on next request — no deploy required',
          hasAuditLog
            ? 'Settings change written to audit_log with before/after values'
            : 'Change logged',
        ],
      },
    ],
    endpoints: [
      {
        id: 'admin-stats',
        method: 'GET',
        path: '/api/admin/stats',
        description:
          'Dashboard KPIs: user count, signups today, pending flags, error rate',
        response:
          '{ totalUsers, activeToday, signupsToday, pendingContent, errorRate }',
      },
      {
        id: 'list-users',
        method: 'GET',
        path: '/api/admin/users',
        description:
          'Paginated, searchable user list — supports ?q=, ?status=, ?plan=, ?page=',
        response: '{ users: User[], total: number }',
      },
      {
        id: 'get-user',
        method: 'GET',
        path: '/api/admin/users/:id',
        description:
          'Full user detail including subscription, sessions, and recent activity',
        response:
          '{ user: User, subscription?, sessions: Session[], recentEvents: Event[] }',
      },
      {
        id: 'update-user-status',
        method: 'PATCH',
        path: '/api/admin/users/:id/status',
        description: 'Update user status — active | suspended | banned',
        requestBody: '{ status, reason, expiresAt? }',
        response: '{ user }',
      },
      {
        id: 'bulk-users',
        method: 'POST',
        path: '/api/admin/users/bulk',
        description: 'Perform a bulk action on multiple users',
        requestBody:
          '{ action: "suspend" | "export" | "email", userIds: string[], params? }',
        response: '{ jobId }',
      },
      ...(hasImpersonation
        ? [
            {
              id: 'impersonate',
              method: 'POST' as const,
              path: '/api/admin/users/:id/impersonate',
              description:
                'Create a short-lived impersonation session token (super-admin only)',
              response: '{ sessionToken, expiresAt }',
            },
          ]
        : []),
      {
        id: 'list-content',
        method: 'GET',
        path: '/api/admin/content',
        description:
          'Paginated flagged content queue — supports ?status=pending|approved|removed',
        response: '{ items: ContentItem[], total: number }',
      },
      {
        id: 'moderate-content',
        method: 'PATCH',
        path: '/api/admin/content/:id',
        description: 'Approve, remove, or escalate a flagged content item',
        requestBody: '{ action: "approve" | "remove" | "escalate", reason? }',
      },
      {
        id: 'update-settings',
        method: 'PATCH',
        path: '/api/admin/settings',
        description: 'Update a site-wide setting or feature flag',
        requestBody: '{ key, value }',
      },
      ...(hasAuditLog
        ? [
            {
              id: 'audit-log',
              method: 'GET' as const,
              path: '/api/admin/audit-log',
              description:
                'Paginated audit log — supports ?actorId=, ?action=, ?resourceType=, ?from=, ?to=',
              response: '{ entries: AuditEntry[], total: number }',
            },
          ]
        : []),
    ],
    dbTables: isMongo
      ? [
          {
            id: 'audit_log',
            name: 'audit_log',
            columns: [
              { name: '_id', type: 'ObjectId', nullable: false },
              {
                name: 'actorId',
                type: 'ObjectId ref users',
                nullable: false,
                notes: 'The admin who performed the action',
              },
              {
                name: 'action',
                type: 'string',
                nullable: false,
                notes: 'e.g. user.suspend, content.remove, settings.update',
              },
              { name: 'resourceType', type: 'string', nullable: true },
              { name: 'resourceId', type: 'string', nullable: true },
              {
                name: 'before',
                type: 'object',
                nullable: true,
                notes: 'Snapshot of resource state before change',
              },
              {
                name: 'after',
                type: 'object',
                nullable: true,
                notes: 'Snapshot of resource state after change',
              },
              { name: 'ipAddress', type: 'string', nullable: true },
              {
                name: 'impersonating',
                type: 'boolean',
                nullable: false,
                notes:
                  'True if action was taken during an impersonation session',
              },
              { name: 'createdAt', type: 'Date', nullable: false },
            ],
            notes:
              'Never allow deletes on this collection. Index { actorId, createdAt } and { resourceType, resourceId, createdAt }.',
          },
          {
            id: 'settings',
            name: 'settings',
            columns: [
              { name: '_id', type: 'ObjectId', nullable: false },
              { name: 'key', type: 'string', nullable: false },
              { name: 'value', type: 'mixed', nullable: false },
              { name: 'updatedAt', type: 'Date', nullable: false },
              { name: 'updatedBy', type: 'ObjectId ref users', nullable: true },
            ],
            notes:
              'Unique index on key. Cache in memory with a short TTL to avoid DB hits per request.',
          },
        ]
      : [
          {
            id: 'audit_log',
            name: 'audit_log',
            columns: [
              { name: 'id', type: 'uuid', nullable: false },
              {
                name: 'actor_id',
                type: 'uuid FK users',
                nullable: false,
                notes: 'The admin who triggered the action',
              },
              {
                name: 'action',
                type: 'text',
                nullable: false,
                notes:
                  'Dot-namespaced action string e.g. user.suspend, content.remove',
              },
              { name: 'resource_type', type: 'text', nullable: true },
              { name: 'resource_id', type: 'text', nullable: true },
              {
                name: 'before',
                type: isPostgres ? 'jsonb' : 'text (JSON)',
                nullable: true,
                notes:
                  'Previous state snapshot for diffing in the audit log UI',
              },
              {
                name: 'after',
                type: isPostgres ? 'jsonb' : 'text (JSON)',
                nullable: true,
                notes: 'New state snapshot',
              },
              { name: 'ip_address', type: 'text', nullable: true },
              {
                name: 'is_impersonated',
                type: 'boolean',
                nullable: false,
                notes: 'True when action was taken via impersonation session',
              },
              { name: 'created_at', type: 'timestamp', nullable: false },
            ],
            notes:
              'Append-only — no UPDATE or DELETE permissions on this table. Index (actor_id, created_at DESC) and (resource_type, resource_id, created_at DESC). Consider partitioning by month in high-traffic apps.',
          },
          {
            id: 'settings',
            name: 'settings',
            columns: [
              {
                name: 'key',
                type: 'text',
                nullable: false,
                notes:
                  'Primary key — snake_case identifier e.g. maintenance_mode',
              },
              {
                name: 'value',
                type: isPostgres ? 'jsonb' : 'text (JSON)',
                nullable: false,
                notes:
                  'Flexible type — booleans, strings, objects all stored as JSON',
              },
              { name: 'updated_at', type: 'timestamp', nullable: false },
              {
                name: 'updated_by',
                type: 'uuid FK users',
                nullable: true,
                notes: 'FK to the admin who last changed this setting',
              },
            ],
            notes:
              'Cache in Redis or in-process memory with a 60s TTL. Invalidate on PATCH /api/admin/settings.',
          },
        ],
  };
};
